/**
 * Interview Section Types
 *
 * This file imports the generated Zod schemas and composes them into a discriminated union.
 *
 * The Zod schemas are generated from JSON Schema, which is generated from Pydantic models.
 * See apps/live-agent-server/interview_sections.py for the source of truth.
 */
import { z } from "zod";
import { contentSectionSchema } from "./generated/content-section.generated.js";
import { confirmationSectionSchema } from "./generated/confirmation-section.generated.js";
import { decisionSectionSchema } from "./generated/decision-section.generated.js";
import { statementSectionSchema } from "./generated/statement-section.generated.js";
import { formSectionSchema } from "./generated/form-section.generated.js";
import { binaryDecisionSectionSchema } from "./generated/binary-decision-section.generated.js";
import { slotSchema } from "./generated/slot.generated.js";
import { formSlotSchema } from "./generated/form-slot.generated.js";
import { validateSectionReferences, findOrphanedSections, detectCircularReferences, validateSlotUniqueness, hasTerminalSection, } from "./interview-sections-validation.js";
// Export schemas directly - refs are now resolved via json-refs
export { slotSchema, formSlotSchema, contentSectionSchema, confirmationSectionSchema, decisionSectionSchema, statementSectionSchema, formSectionSchema, binaryDecisionSectionSchema, // Deprecated alias for backward compatibility
 };
// Create union for interview sections
// Using z.discriminatedUnion to provide better error messages for invalid section types
export const interviewSectionSchema = z.discriminatedUnion("section_type", [
    contentSectionSchema,
    confirmationSectionSchema,
    decisionSectionSchema,
    statementSectionSchema,
    formSectionSchema,
]);
// Array of interview sections (basic schema without validation)
export const interviewSectionsSchema = z.array(interviewSectionSchema);
// Enhanced schema with section-specific validations
const contentSectionWithValidation = contentSectionSchema.superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (section.quality_threshold !== null &&
        section.quality_threshold !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "quality_threshold" is deprecated and must be null or undefined`,
            path: ["quality_threshold"],
        });
    }
    // Validate slots if present
    if (section.slots && section.slots.length > 0) {
        // Validate slot uniqueness
        const { errors } = validateSlotUniqueness(section.slots, section.id);
        for (const error of errors) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: error,
                path: ["slots"],
            });
        }
        // Validate each slot has required fields
        section.slots.forEach((slot, index) => {
            if (!slot.name || slot.name.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot at index ${index} must have a non-empty name`,
                    path: ["slots", index, "name"],
                });
            }
            if (!slot.description || slot.description.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a non-empty description`,
                    path: ["slots", index, "description"],
                });
            }
        });
    }
    // Must have non-null, non-empty purpose
    if (!section.purpose || section.purpose.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Content sections must have a non-empty purpose`,
            path: ["purpose"],
        });
    }
    // Must have next_section (cannot be terminal)
    if (section.next_section === null || section.next_section === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Content sections must be connected to another section and cannot be terminal. Use message sections as terminal sections.`,
            path: ["next_section"],
        });
    }
    // Should not have decision_slot or choices
    if ("decision_slot" in section && section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Content sections should not have a decision_slot field`,
            path: ["decision_slot"],
        });
    }
    if ("choices" in section && section.choices) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Content sections should not have a choices field`,
            path: ["choices"],
        });
    }
});
const confirmationSectionWithValidation = confirmationSectionSchema
    .passthrough()
    .superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (section.yes_path && section.yes_path !== "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "yes_path" is deprecated. Use "choices" array instead`,
            path: ["yes_path"],
        });
    }
    if (section.no_path && section.no_path !== "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "no_path" is deprecated. Use "choices" array instead`,
            path: ["no_path"],
        });
    }
    // Must have choices array
    if (!section.choices || section.choices.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Confirmation sections must have a choices array`,
            path: ["choices"],
        });
    }
    else {
        // Must have exactly 2 choices
        if (section.choices.length !== 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": Confirmation sections must have exactly 2 choices (yes and no)`,
                path: ["choices"],
            });
        }
        // Must have "yes" and "no" choices
        const choiceNames = section.choices.map((c) => c.name.toLowerCase());
        if (!choiceNames.includes("yes") || !choiceNames.includes("no")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": Confirmation sections must have choices named "yes" and "no"`,
                path: ["choices"],
            });
        }
        // Each choice must have a target
        section.choices.forEach((choice, index) => {
            if (!choice.target || choice.target.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Choice "${choice.name}" must be connected to another section.`,
                    path: ["choices", index, "target"],
                });
            }
        });
    }
    // Must have decision_slot
    if (!section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Confirmation sections must have a decision_slot`,
            path: ["decision_slot"],
        });
    }
    else {
        // decision_slot must have name and description (scripted_questions are optional)
        if (!section.decision_slot.name ||
            section.decision_slot.name.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty name`,
                path: ["decision_slot", "name"],
            });
        }
        if (!section.decision_slot.description ||
            section.decision_slot.description.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty description`,
                path: ["decision_slot", "description"],
            });
        }
    }
    // Should not have slots
    if ("slots" in section && section.slots) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Confirmation sections should not have a slots field`,
            path: ["slots"],
        });
    }
    // Should not have next_section (routing is via choices)
    if ("next_section" in section && section.next_section) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Confirmation sections should not have a next_section field (routing is via choices)`,
            path: ["next_section"],
        });
    }
});
const decisionSectionWithValidation = decisionSectionSchema.superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    // Must have choices array with at least 2 items
    if (!section.choices || section.choices.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Decision sections must have a choices array`,
            path: ["choices"],
        });
    }
    else if (section.choices.length < 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Decision sections must have at least 2 choices`,
            path: ["choices"],
        });
    }
    else {
        // Validate unique choice names
        const names = new Set();
        section.choices.forEach((choice, index) => {
            if (names.has(choice.name)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Duplicate choice name "${choice.name}"`,
                    path: ["choices", index, "name"],
                });
            }
            names.add(choice.name);
            // Each choice must have a target
            if (!choice.target || choice.target.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Choice "${choice.name}" must be connected to another section.`,
                    path: ["choices", index, "target"],
                });
            }
        });
    }
    // Must have decision_slot
    if (!section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Decision sections must have a decision_slot`,
            path: ["decision_slot"],
        });
    }
    else {
        // decision_slot must have name and description (scripted_questions are optional)
        if (!section.decision_slot.name ||
            section.decision_slot.name.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty name`,
                path: ["decision_slot", "name"],
            });
        }
        if (!section.decision_slot.description ||
            section.decision_slot.description.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty description`,
                path: ["decision_slot", "description"],
            });
        }
    }
    // Should not have slots
    if ("slots" in section && section.slots) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Decision sections should not have a slots field`,
            path: ["slots"],
        });
    }
    // Should not have next_section (routing is via choices)
    if ("next_section" in section && section.next_section) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Decision sections should not have a next_section field (routing is via choices)`,
            path: ["next_section"],
        });
    }
});
const formSectionWithValidation = formSectionSchema.superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    // Must have at least 1 slot
    if (!section.slots || section.slots.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Form sections must have at least 1 slot`,
            path: ["slots"],
        });
    }
    else {
        // Validate slot uniqueness
        const { errors } = validateSlotUniqueness(section.slots, section.id);
        for (const error of errors) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: error,
                path: ["slots"],
            });
        }
        // Validate each slot has required fields
        section.slots.forEach((slot, index) => {
            if (!slot.name || slot.name.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot at index ${index} must have a non-empty name`,
                    path: ["slots", index, "name"],
                });
            }
            if (!slot.type) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a type`,
                    path: ["slots", index, "type"],
                });
            }
            if (!slot.label || slot.label.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a non-empty label`,
                    path: ["slots", index, "label"],
                });
            }
            if (slot.required === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a required field (boolean)`,
                    path: ["slots", index, "required"],
                });
            }
        });
    }
    // Should not have decision_slot or choices
    if ("decision_slot" in section && section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Form sections should not have a decision_slot field`,
            path: ["decision_slot"],
        });
    }
    if ("choices" in section && section.choices) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Form sections should not have a choices field`,
            path: ["choices"],
        });
    }
});
const statementSectionWithValidation = statementSectionSchema
    .passthrough()
    .superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    // Must have non-empty instructions
    if (!section.instructions || section.instructions.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Statement sections must have non-empty instructions`,
            path: ["instructions"],
        });
    }
    // Should not have slots, decision_slot, or choices
    if ("slots" in section && section.slots) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Statement sections should not have a slots field`,
            path: ["slots"],
        });
    }
    if ("decision_slot" in section && section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Statement sections should not have a decision_slot field`,
            path: ["decision_slot"],
        });
    }
    if ("choices" in section && section.choices) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Statement sections should not have a choices field`,
            path: ["choices"],
        });
    }
});
// Enhanced interview section schema with validations
const interviewSectionSchemaWithValidation = z.discriminatedUnion("section_type", [
    contentSectionWithValidation,
    confirmationSectionWithValidation,
    decisionSectionWithValidation,
    statementSectionWithValidation,
    formSectionWithValidation,
]);
// Enhanced schema with cross-section validations
export const interviewSectionsSchemaWithValidation = z
    .array(interviewSectionSchemaWithValidation)
    .superRefine((sections, ctx) => {
    // Must have at least one section
    if (sections.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Interview must have at least one section",
        });
        return;
    }
    // Validate unique section IDs
    const ids = new Set();
    sections.forEach((section, index) => {
        if (ids.has(section.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate section ID: "${section.id}"`,
                path: [index, "id"],
            });
        }
        ids.add(section.id);
    });
    // Validate section references
    const { errors: refErrors } = validateSectionReferences(sections);
    for (const error of refErrors) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
        });
    }
    // Check for at least one terminal section
    if (!hasTerminalSection(sections)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Interview must have at least one terminal section (with next_section: null)",
        });
    }
    // Check for orphaned sections (warning level)
    const { warnings: orphanWarnings } = findOrphanedSections(sections);
    for (const warning of orphanWarnings) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] ${warning}`,
        });
    }
    // Check for circular references (warning level)
    const { warnings: circularWarnings } = detectCircularReferences(sections);
    for (const warning of circularWarnings) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] ${warning}`,
        });
    }
});
// ============================================================================
// STRICT SCHEMAS (New section type names only)
// ============================================================================
// These schemas ONLY accept the new section type names:
// - explore (was: content)
// - gate (was: confirmation)
// - split (was: decision)
// - message (was: statement)
// - collect (was: form)
//
// Use these for template editor validation to enforce new naming convention.
// ============================================================================
/**
 * Mapping from old section type names to new names
 */
export const SECTION_TYPE_OLD_TO_NEW = {
    content: "explore",
    confirmation: "gate",
    decision: "split",
    statement: "message",
    form: "collect",
};
/**
 * Mapping from new section type names to old names
 */
export const SECTION_TYPE_NEW_TO_OLD = {
    explore: "content",
    gate: "confirmation",
    split: "decision",
    message: "statement",
    collect: "form",
};
// Create strict schemas that only accept new section type names
// These extend the base schemas but override the section_type field
const exploreSectionSchemaBase = contentSectionSchema
    .omit({ section_type: true })
    .extend({
    section_type: z.literal("explore"),
});
const gateSectionSchemaBase = confirmationSectionSchema
    .omit({ section_type: true })
    .extend({
    section_type: z.literal("gate"),
});
const splitSectionSchemaBase = decisionSectionSchema
    .omit({ section_type: true })
    .extend({
    section_type: z.literal("split"),
});
const messageSectionSchemaBase = statementSectionSchema
    .omit({ section_type: true })
    .extend({
    section_type: z.literal("message"),
});
const collectSectionSchemaBase = formSectionSchema
    .omit({ section_type: true })
    .extend({
    section_type: z.literal("collect"),
});
// Export the strict base schemas
export const exploreSectionSchema = exploreSectionSchemaBase;
export const gateSectionSchema = gateSectionSchemaBase;
export const splitSectionSchema = splitSectionSchemaBase;
export const messageSectionSchema = messageSectionSchemaBase;
export const collectSectionSchema = collectSectionSchemaBase;
// Strict interview section schema (new names only)
export const interviewSectionSchemaStrict = z.discriminatedUnion("section_type", [
    exploreSectionSchemaBase,
    gateSectionSchemaBase,
    splitSectionSchemaBase,
    messageSectionSchemaBase,
    collectSectionSchemaBase,
]);
// Add validations to strict schemas (same logic as old names)
const exploreSectionWithValidation = exploreSectionSchemaBase.superRefine((section, ctx) => {
    // Deprecated fields enforcement
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (section.quality_threshold !== null &&
        section.quality_threshold !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "quality_threshold" is deprecated and must be null or undefined`,
            path: ["quality_threshold"],
        });
    }
    // Validate slots if present
    if (section.slots && section.slots.length > 0) {
        const { errors } = validateSlotUniqueness(section.slots, section.id);
        for (const error of errors) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: error,
                path: ["slots"],
            });
        }
        section.slots.forEach((slot, index) => {
            if (!slot.name || slot.name.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot at index ${index} must have a non-empty name`,
                    path: ["slots", index, "name"],
                });
            }
            if (!slot.description || slot.description.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a non-empty description`,
                    path: ["slots", index, "description"],
                });
            }
        });
    }
    if (!section.purpose || section.purpose.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Explore sections must have a non-empty purpose`,
            path: ["purpose"],
        });
    }
    if (section.next_section === null || section.next_section === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Explore sections must be connected to another section and cannot be terminal. Use message sections as terminal sections.`,
            path: ["next_section"],
        });
    }
});
const gateSectionWithValidation = gateSectionSchemaBase
    .passthrough()
    .superRefine((section, ctx) => {
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (section.yes_path && section.yes_path !== "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "yes_path" is deprecated. Use "choices" array instead`,
            path: ["yes_path"],
        });
    }
    if (section.no_path && section.no_path !== "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "no_path" is deprecated. Use "choices" array instead`,
            path: ["no_path"],
        });
    }
    if (!section.choices || section.choices.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Gate sections must have a choices array`,
            path: ["choices"],
        });
    }
    else {
        if (section.choices.length !== 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": Gate sections must have exactly 2 choices (yes and no)`,
                path: ["choices"],
            });
        }
        const choiceNames = section.choices.map((c) => c.name.toLowerCase());
        if (!choiceNames.includes("yes") || !choiceNames.includes("no")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": Gate sections must have choices named "yes" and "no"`,
                path: ["choices"],
            });
        }
        section.choices.forEach((choice, index) => {
            if (!choice.target || choice.target.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Choice "${choice.name}" must be connected to another section.`,
                    path: ["choices", index, "target"],
                });
            }
        });
    }
    if (!section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Gate sections must have a decision_slot`,
            path: ["decision_slot"],
        });
    }
    else {
        if (!section.decision_slot.name ||
            section.decision_slot.name.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty name`,
                path: ["decision_slot", "name"],
            });
        }
        if (!section.decision_slot.description ||
            section.decision_slot.description.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty description`,
                path: ["decision_slot", "description"],
            });
        }
    }
});
const splitSectionWithValidation = splitSectionSchemaBase.superRefine((section, ctx) => {
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (!section.choices || section.choices.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Split sections must have a choices array`,
            path: ["choices"],
        });
    }
    else if (section.choices.length < 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Split sections must have at least 2 choices`,
            path: ["choices"],
        });
    }
    else {
        const names = new Set();
        section.choices.forEach((choice, index) => {
            if (names.has(choice.name)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Duplicate choice name "${choice.name}"`,
                    path: ["choices", index, "name"],
                });
            }
            names.add(choice.name);
            if (!choice.target || choice.target.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Choice "${choice.name}" must be connected to another section.`,
                    path: ["choices", index, "target"],
                });
            }
        });
    }
    if (!section.decision_slot) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Split sections must have a decision_slot`,
            path: ["decision_slot"],
        });
    }
    else {
        if (!section.decision_slot.name ||
            section.decision_slot.name.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty name`,
                path: ["decision_slot", "name"],
            });
        }
        if (!section.decision_slot.description ||
            section.decision_slot.description.trim() === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Section "${section.id}": decision_slot must have a non-empty description`,
                path: ["decision_slot", "description"],
            });
        }
    }
});
const collectSectionWithValidation = collectSectionSchemaBase.superRefine((section, ctx) => {
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (!section.slots || section.slots.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Collect sections must have at least 1 slot`,
            path: ["slots"],
        });
    }
    else {
        const { errors } = validateSlotUniqueness(section.slots, section.id);
        for (const error of errors) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: error,
                path: ["slots"],
            });
        }
        section.slots.forEach((slot, index) => {
            if (!slot.name || slot.name.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot at index ${index} must have a non-empty name`,
                    path: ["slots", index, "name"],
                });
            }
            if (!slot.type) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a type`,
                    path: ["slots", index, "type"],
                });
            }
            if (!slot.label || slot.label.trim() === "") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a non-empty label`,
                    path: ["slots", index, "label"],
                });
            }
            if (slot.required === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Section "${section.id}": Slot "${slot.name}" must have a required field (boolean)`,
                    path: ["slots", index, "required"],
                });
            }
        });
    }
});
const messageSectionWithValidation = messageSectionSchemaBase
    .passthrough()
    .superRefine((section, ctx) => {
    if (section.priority !== null && section.priority !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Field "priority" is deprecated and must be null or undefined`,
            path: ["priority"],
        });
    }
    if (!section.instructions || section.instructions.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Section "${section.id}": Message sections must have non-empty instructions`,
            path: ["instructions"],
        });
    }
});
// Strict interview section schema with validation (new names only)
const interviewSectionSchemaStrictWithValidation = z.discriminatedUnion("section_type", [
    exploreSectionWithValidation,
    gateSectionWithValidation,
    splitSectionWithValidation,
    messageSectionWithValidation,
    collectSectionWithValidation,
]);
// Enhanced strict schema with cross-section validations
export const interviewSectionsSchemaWithValidationStrict = z
    .array(interviewSectionSchemaStrictWithValidation)
    .superRefine((sections, ctx) => {
    if (sections.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Interview must have at least one section",
        });
        return;
    }
    const ids = new Set();
    sections.forEach((section, index) => {
        if (ids.has(section.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate section ID: "${section.id}"`,
                path: [index, "id"],
            });
        }
        ids.add(section.id);
    });
    const { errors: refErrors } = validateSectionReferences(sections);
    for (const error of refErrors) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
        });
    }
    if (!hasTerminalSection(sections)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Interview must have at least one terminal section (with next_section: null)",
        });
    }
    // Warning: First section should have id="intro"
    const firstSection = sections[0];
    if (firstSection && firstSection.id !== "intro") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] First section should have id="intro" (found "${firstSection.id}"). This is the required entry point for conversations.`,
            path: [0, "id"],
        });
    }
    // Warning: Should have a terminal section with id="goodbye"
    const hasGoodbyeTerminal = sections.some((section) => section.id === "goodbye" &&
        "next_section" in section &&
        section.next_section === null);
    if (!hasGoodbyeTerminal) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] Interview should have a terminal section with id="goodbye" (section_type: message, next_section: null). This serves as the universal exit point for both normal endings and early exits.`,
        });
    }
    // Warning: Consecutive message sections (e.g., "thank_you" -> "goodbye")
    // Message sections should be terminal (next_section: null) or lead to interactive sections
    const sectionById = new Map(sections.map((s) => [s.id, s]));
    for (const section of sections) {
        if (section.section_type === "message" &&
            "next_section" in section &&
            section.next_section !== null) {
            const targetSection = sectionById.get(section.next_section);
            if (targetSection && targetSection.section_type === "message") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `[Warning] Section "${section.id}" (message) chains to "${section.next_section}" (message). Avoid consecutive message sections - combine them into one message section, or make "${section.id}" terminal (next_section: null) if it's an ending.`,
                });
            }
        }
    }
    const { warnings: orphanWarnings } = findOrphanedSections(sections);
    for (const warning of orphanWarnings) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] ${warning}`,
        });
    }
    const { warnings: circularWarnings } = detectCircularReferences(sections);
    for (const warning of circularWarnings) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[Warning] ${warning}`,
        });
    }
});
// ============================================================================
// SECTION TYPE CONSTANTS
// ============================================================================
// Use these constants instead of string literals for type safety and consistency
/**
 * Old section type names (currently used in the database)
 * Use these when working with actual section data
 */
export const SECTION_TYPES = {
    CONTENT: "content",
    CONFIRMATION: "confirmation",
    DECISION: "decision",
    STATEMENT: "statement",
    FORM: "form",
};
/**
 * New section type names (used in UI/template editor)
 * Use these for display purposes and new code
 */
export const SECTION_TYPES_NEW = {
    EXPLORE: "explore",
    GATE: "gate",
    SPLIT: "split",
    MESSAGE: "message",
    COLLECT: "collect",
};
